// src/pages/LightFix.jsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateLightFixModal from "../components/GenerateLightFixModal";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import Loader from "../components/Loader";

const FEATURE_KEY = "light-fix";
const PAGE_SIZE = 12;

export default function LightFix() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  // ✅ Prefer URL param as source of truth (prevents ping-pong when navigating via notifications)
  const projectId = paramProjectId || selectedProduct?.id;

  // items: [{ id, url, status, created_at }]
  const [items, setItems] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { id, url }
  const [error, setError] = useState(null);

  // per-user image quota (for greying out CTA + pill)
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,
    blocked: false,
    paymentBlocked: false,
  });

  // ✅ infinite scroll state (MATCH ObjectRemover pattern)
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  const previewBackdrop = useRef(null);
  const pollRef = useRef(null);

  /* --------------------------- Helpers --------------------------- */
  const toArray = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      const t = val.trim();
      if (!t || t === "{}") return [];
      if (t.startsWith("{") && t.endsWith("}")) {
        return t
          .slice(1, -1)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      try {
        const parsed = JSON.parse(t);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  const pickLightFixAssets = useCallback(
    (data) => data?.assetsByFeature?.[FEATURE_KEY] || data?.assets || [],
    []
  );

  const getMeta = useCallback((m) => {
    if (!m) return {};
    if (typeof m === "string") {
      try {
        return JSON.parse(m);
      } catch {
        return {};
      }
    }
    return m;
  }, []);

  // Cloudinary direct-download helper (no new tab)
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore parse errors
    }
    return url;
  }, []);

  // ✅ Match BackgroundReplacer behavior:
  // - hide soft-deleted + failed rows (must remain in DB for quota)
  // - treat placeholder URLs (with #fragment) and empty URLs as pending
  const mapAssets = useCallback(
    (rows) =>
      (rows || [])
        .map((a) => {
          const meta = getMeta(a.metadata);
          const statusRaw = String(meta?.status || "finished").toLowerCase();
          const feature = String(meta?.feature || "").toLowerCase();

          const isDeleted = meta?.deleted === true || statusRaw === "deleted";
          const isFailed = statusRaw === "failed";
          if (isDeleted || isFailed) return null;

          const rawUrl = a?.url || "";
          const isPending =
            statusRaw === "pending" ||
            statusRaw === "queued" ||
            statusRaw === "processing" ||
            !rawUrl ||
            rawUrl === "/spinner.gif" ||
            String(rawUrl).includes("#");

          return {
            id: a.id,
            url: isPending ? "/spinner.gif" : rawUrl,
            status: isPending ? statusRaw || "pending" : "finished",
            feature,
            created_at: a.created_at || a.createdAt || 0,
          };
        })
        .filter(Boolean)
        .filter((a) => a.feature === FEATURE_KEY)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [getMeta]
  );

  const anyPending = useMemo(() => {
    const P = new Set(["pending", "queued", "processing"]);
    return items.some((i) => P.has(i.status) || !i.url || i.url === "/spinner.gif");
  }, [items]);

  const visibleItems = useMemo(
    () => items.slice(0, page * PAGE_SIZE),
    [items, page]
  );

  // ✅ MATCH ObjectRemover: stable recalc that does NOT depend on `page`
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  // ✅ keep pageRef in sync
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // ✅ keep hasMore in sync whenever page/items change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < items.length);
  }, [page, items.length]);

  /* ----------------------------- Quota ----------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/light-fix/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  // ✅ Quota-only gating (Remove BG style)
  const canGenerateByQuota =
    !quota.blocked &&
    !quota.paymentBlocked &&
    (quota.unlimited || (quota.remaining || 0) > 0);

  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  /* --------------------------- Fetch project --------------------------- */
  const fetchProjectData = useCallback(async () => {
    // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
    if (!projectId) {
      setProductImages([]);
      setItems((prev) => prev.filter((p) => String(p.id).startsWith("temp-"))); // keep optimistic only
      setError(null);
      setPage(1);
      pageRef.current = 1;
      setHasMore(false);
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);
    setPage(1);
    pageRef.current = 1;

    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProductImages(toArray(res.data.images));

      const serverList = mapAssets(pickLightFixAssets(res.data));

      setItems((prev) => {
        const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
        const serverById = new Map(serverList.map((a) => [String(a.id), a]));
        const keptTemp = temps.length ? [temps[0]] : [];
        const tempNotDuplicated = keptTemp.filter(
          (t) => !serverById.has(String(t.id))
        );
        const merged = [...tempNotDuplicated, ...serverList];
        recalcHasMore(merged, 1);
        return merged;
      });

      setError(null);
    } catch (e) {
      setError(
        e?.response?.status === 401
          ? "Session expired. Please sign in again."
          : "Failed to fetch project data"
      );
    } finally {
      setInitialLoading(false);
    }
  }, [getToken, projectId, recalcHasMore, mapAssets, pickLightFixAssets, toArray]);

  useEffect(() => {
    void fetchProjectData();
    void fetchQuota();
  }, [fetchProjectData, fetchQuota]);

  // ✅ Only sync URL from dropdown when URL has no projectId (prevents notification bounce)
  useEffect(() => {
    if (!paramProjectId && selectedProduct?.id) {
      navigate(`/generate/light-fix/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  // Clear poll on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  /* ----------------------- Polling (auto-refresh) ----------------------- */
  const beginPolling = useCallback(() => {
    if (!projectId) return;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    let attempts = 0;

    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const token = await getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const serverList = mapAssets(pickLightFixAssets(res.data));

        setItems((prev) => {
          const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
          const serverById = new Map(serverList.map((a) => [String(a.id), a]));
          const keptTemp = temps.length ? [temps[0]] : [];
          const tempNotDuplicated = keptTemp.filter(
            (t) => !serverById.has(String(t.id))
          );
          const merged = [...tempNotDuplicated, ...serverList];
          recalcHasMore(merged, pageRef.current);
          return merged;
        });

        const stillPending = serverList.some((a) => {
          const s = a.status;
          return s && s !== "finished" && s !== "done";
        });

        if (!stillPending || attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          void fetchQuota();
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          setError("Session expired. Please sign in again.");
          clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
        if (attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          void fetchQuota();
        }
      }
    }, 5000);
  }, [getToken, projectId, recalcHasMore, mapAssets, pickLightFixAssets, fetchQuota]);

  useEffect(() => {
    if (anyPending) beginPolling();
  }, [anyPending, beginPolling]);

  /* ------------------------ Generate (single temp) ------------------------ */
  const handleGenerate = useCallback(
    async (imageUrl, quality) => {
      setError(null);

      // ✅ No project yet → send user to Analyze (still shows CTA on the page)
      if (!projectId) {
        setError("Please analyze a product first so we can save your generated assets.");
        navigate("/analyze");
        return;
      }

      if (quota.blocked) {
        setError("Your account is blocked. Please contact support.");
        return;
      }

      if (quota.paymentBlocked) {
        setError("Your subscription payment is past due. Please update billing to continue.");
        navigate("/pricing");
        return;
      }

      if (!quota.unlimited && (quota.remaining || 0) <= 0) {
        setError("You’ve reached your image limit for this cycle. Add an Images add-on or upgrade.");
        navigate("/pricing");
        return;
      }

      const tempId = "temp-lightfix";
      const alreadyHasTemp = items.some((i) => String(i.id) === tempId);
      if (!alreadyHasTemp) {
        const nextList = [{ id: tempId, url: "/spinner.gif", status: "pending" }, ...items];
        setItems(nextList);
        recalcHasMore(nextList, pageRef.current);
      }

      beginPolling();

      try {
        const token = await getToken();
        const queueRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/light-fix/fix`,
          {
            imageUrl,
            projectId,
            quality: (quality || "2k").toLowerCase(),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const assetId = queueRes.data?.assetId;

        if (assetId) {
          setItems((prev) =>
            prev.map((it) =>
              String(it.id) === tempId
                ? { ...it, id: String(assetId), status: "queued", url: "/spinner.gif" }
                : it
            )
          );
        }

        // Optimistic quota decrement (pending counts as used)
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: Math.max(0, (q.remaining || 0) - 1),
            used: (q.used || 0) + 1,
          }));
        }
      } catch (e) {
        // Remove temp spinner on immediate failure
        setItems((prev) => {
          const next = prev.filter((x) => String(x.id) !== tempId);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        const code = e?.response?.data?.code;
        if (code === "images_quota_exceeded" || code === "payment_required") {
          navigate("/pricing");
        }

        setError(e?.response?.data?.error || "Failed to fix lighting. Please try again.");
      }
    },
    [getToken, projectId, beginPolling, items, recalcHasMore, quota, navigate]
  );

  /* ------------------------------ Download -------------------------------- */
  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  /* -------------------------------- Delete -------------------------------- */
  const handleDelete = useCallback(
    async (assetId) => {
      if (String(assetId) === "temp-lightfix" || String(assetId).startsWith("temp-")) {
        setItems((prev) => {
          const next = prev.filter((img) => String(img.id) !== String(assetId));
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;

      try {
        const token = await getToken();

        // ✅ IMPORTANT: use feature soft-delete endpoint so quota does NOT decrease
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/light-fix/delete`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { projectId, assetId },
        });

        setItems((prev) => {
          const next = prev.filter((img) => img.id !== assetId);
          if (preview?.id === assetId) setPreview(null);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // quota should remain unchanged, but keep UI synced
        void fetchQuota();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Delete failed", e?.response?.data || e?.message);
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, preview, recalcHasMore, fetchQuota, projectId]
  );

  /* --------------------------- Infinite Scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  /* --------------------------- Preview events --------------------------- */
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  const isInitialLoading = initialLoading && !error && items.length === 0;

  /* -------------------------------- Render -------------------------------- */
  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Light Fix</h1>

          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : quota.blocked
                  ? "border-red-500/40 bg-red-500/10"
                  : quota.paymentBlocked
                  ? "border-amber-500/40 bg-amber-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : quota.blocked
                  ? "Account blocked"
                  : quota.paymentBlocked
                  ? "Billing issue"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : quota.blocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Account blocked
                </span>
              ) : quota.paymentBlocked ? (
                <span className="text-amber-300 font-semibold text-sm sm:text-base">
                  Billing issue
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span> left of{" "}
                  <span className="font-semibold text-white">
                    {Number.isFinite(totalImages) ? totalImages : "∞"}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-3">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {Number.isFinite(totalImages) ? totalImages : "∞"}</span>
            </div>
          </div>
        )}

        <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-base">
          Automatically correct under/overexposed product photos using AI.
        </p>

        {error && (
          <div className="mb-4 sm:mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {isInitialLoading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : (
          <div
            className="
              grid gap-4 sm:gap-6
              grid-cols-1
              xs:grid-cols-2
              md:grid-cols-3
              lg:grid-cols-4
              [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
            "
          >
            {/* ✅ CTA always visible (Remove BG logic) */}
            <div
              onClick={() => {
                // no analyzed product -> Analyze
                if (!projectId) {
                  navigate("/analyze");
                  return;
                }

                // account blocked (not solvable by pricing)
                if (quota.blocked) {
                  setError("Your account is blocked. Please contact support.");
                  return;
                }

                // out of quota / billing -> Pricing
                if (!canGenerateByQuota) {
                  navigate("/pricing");
                  return;
                }

                setModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] transition text-center p-4 sm:p-6 ${
                !projectId || !canGenerateByQuota || quota.blocked
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
              }`}
              title={
                !projectId
                  ? "Analyze a product first to start generating"
                  : quota.blocked
                  ? "Account blocked"
                  : !canGenerateByQuota
                  ? quota.paymentBlocked
                    ? "Billing issue. Click to manage plan."
                    : "Out of images. Click to manage plan."
                  : "Choose an image and quality (2K/4K)"
              }
            >
              <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
              <p className="font-semibold text-base">Fix Image Lighting</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                {!projectId
                  ? "Analyze your first product to get started"
                  : quota.unlimited
                  ? "Unlimited images"
                  : quota.blocked
                  ? "Contact support to regain access"
                  : quota.paymentBlocked
                  ? "Payment required to continue"
                  : `You have ${remaining} left of ${
                      Number.isFinite(totalImages) ? totalImages : "∞"
                    } this cycle`}
              </p>

              {!!projectId && !canGenerateByQuota && !quota.blocked && (
                <p className="text-xs text-red-300 mt-2">
                  {quota.paymentBlocked
                    ? "Your subscription needs attention. Update payment to continue."
                    : "You’ve reached your image limit. Upgrade or add images."}
                </p>
              )}

              {!!projectId && quota.blocked && (
                <p className="text-xs text-red-300 mt-2">
                  Your account is blocked. Contact support to continue.
                </p>
              )}
            </div>

            {visibleItems.map(({ id, url, status }) => {
              const PENDING_STATES = new Set(["pending", "queued", "processing"]);
              const isPending =
                PENDING_STATES.has(status) || !url || url === "/spinner.gif";
              const displayUrl = isPending ? "/spinner.gif" : url;

              return (
                <div
                  key={id}
                  className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center"
                  style={{ overflow: "hidden" }}
                >
                  {isPending ? (
                    <>
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDelete(id);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={displayUrl}
                        alt="Light Fix Result"
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => setPreview({ id, url: displayUrl })}
                        onError={(e) => {
                          e.currentTarget.src = placeholderImage;
                        }}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(displayUrl);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDelete(id);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !initialLoading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img src="/spinner.gif" alt="Loading..." className="w-7 h-7 mx-auto" />
            )}
          </div>
        )}

        {modalOpen && (
          <GenerateLightFixModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
            projectId={projectId}
          />
        )}

        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl p-0 flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-4">{error}</div>}
      </div>
    </div>
  );
}
